import { useMemo } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import { map, find, get, intersection } from 'lodash'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../../shared/components/formik/FormikSelectField'

const IngressForm = ({
  ingress,
  ingressControllers,
  domains,
  projectNodeIds,
  projectsById,
  createIngress,
  updateIngress,
  onSuccess,
  onCancel
}) => {
  const intl = useIntl()
  const isEditMode = Boolean(ingress)

  // Filter ingress controllers to those with projects sharing nodes with the service's project
  const filteredIngressControllers = useMemo(() => {
    return ingressControllers.filter((ic) => {
      const icProject = get(projectsById, ic.projectId)
      const icNodeIds = get(icProject, 'nodeIds', [])
      const sharedNodes = intersection(projectNodeIds, icNodeIds)
      return sharedNodes.length > 0
    })
  }, [ingressControllers, projectsById, projectNodeIds])

  const ingressControllerOptions = useMemo(() => {
    return map(filteredIngressControllers, (ic) => ({
      value: ic.id,
      label: ic.name
    }))
  }, [filteredIngressControllers])

  const domainOptions = useMemo(() => {
    return map(domains, (domain) => ({
      value: domain.id,
      label: domain.status === 'pending'
        ? `${domain.domain} (${intl.formatMessage({ id: 'domains.status.pending' })})`
        : domain.domain
    }))
  }, [domains, intl])

  const validationSchema = Yup.object({
    ingressControllerId: Yup.string().required(intl.formatMessage({ id: 'validation.required' })),
    domainIds: Yup.array()
      .of(Yup.string())
      .min(1, intl.formatMessage({ id: 'validation.required' }))
      .required(intl.formatMessage({ id: 'validation.required' })),
    path: Yup.string(),
    port: Yup.number()
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .min(1, intl.formatMessage({ id: 'validation.minNumber' }, { min: 1 }))
      .max(65535, intl.formatMessage({ id: 'validation.maxNumber' }, { max: 65535 }))
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ingressControllerId: ingress?.ingressControllerId || '',
      domainIds: ingress?.domainIds || [],
      path: ingress?.path || '',
      port: ingress?.port || ''
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const ingressData = {
        ingress: {
          ingressControllerId: values.ingressControllerId,
          domainIds: values.domainIds,
          path: values.path,
          port: values.port || null
        }
      }

      const apiCall = isEditMode
        ? updateIngress(ingress.id, ingressData)
        : createIngress(ingressData)

      apiCall
        .then(() => {
          onSuccess()
        })
        .catch((err) => {
          setStatus(err.message || 'Error saving ingress')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  // Check if any selected domain is pending
  const hasPendingDomain = useMemo(() => {
    return formik.values.domainIds.some(id => {
      const domain = find(domains, { id })
      return domain?.status === 'pending'
    })
  }, [domains, formik.values.domainIds])

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <FormikSelectField
        formik={formik}
        name="ingressControllerId"
        label={<FormattedMessage id="ingresses.ingressControllerLabel" />}
        options={ingressControllerOptions}
      />

      <Box sx={{ mt: 2 }}>
        <FormikSelectField
          formik={formik}
          name="domainIds"
          label={<FormattedMessage id="ingresses.domainsLabel" />}
          options={domainOptions}
          multiple
        />
        {hasPendingDomain && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <FormattedMessage id="ingresses.hints.pendingDomain" />
          </Alert>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikTextField
          formik={formik}
          name="path"
          label={<FormattedMessage id="ingresses.pathLabel" />}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikTextField
          formik={formik}
          name="port"
          label={<FormattedMessage id="ingresses.portLabel" />}
          type="number"
        />
      </Box>

      {formik.status && (
        <Typography color="error" sx={{ mt: 2 }}>
          {formik.status}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id={isEditMode ? 'ingresses.edit.submitButton' : 'ingresses.create.submitButton'} />
        </Button>
      </Box>
    </Box>
  )
}

export default IngressForm
